import { useState, useCallback, useRef } from "react";
import {
  fetchPropertyListings,
  createPropertyListing,
  updatePropertyListing,
  deletePropertyListing,
  publishPropertyListing,
  unpublishPropertyListing,
} from "../api/propertyListingsApi";

// ── usePropertyListings ──────────────────────────────────────────────────────
export function usePropertyListings() {
  const [data, setData] = useState(null); // { listings, meta }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetch = useCallback(async (filters = {}) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    setError(null);
    try {
      const result = await fetchPropertyListings(filters);
      setData(result);
    } catch (err) {
      if (err.name !== "AbortError") setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback((filters) => fetch(filters), [fetch]);

  return { data, loading, error, fetch, refresh };
}

// ── useCreatePropertyListing ─────────────────────────────────────────────────
export function useCreatePropertyListing() {
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);

  const mutate = useCallback(async (payload, photos) => {
    setStatus("pending");
    setError(null);
    try {
      const result = await createPropertyListing(payload, photos);
      setStatus("success");
      return result;
    } catch (err) {
      const msg =
        err.response?.data?.errors || [err.message || "Something went wrong"];
      setError(msg);
      setStatus("error");
      throw msg;
    }
  }, []);

  return {
    mutate,
    status,
    error,
    isPending: status === "pending",
    reset: () => setStatus("idle"),
  };
}

// ── useUpdatePropertyListing ─────────────────────────────────────────────────
export function useUpdatePropertyListing() {
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);

  const mutate = useCallback(async (id, payload, photos) => {
    setStatus("pending");
    setError(null);
    try {
      const result = await updatePropertyListing(id, payload, photos);
      setStatus("success");
      return result;
    } catch (err) {
      const msg =
        err.response?.data?.errors || [err.message || "Something went wrong"];
      setError(msg);
      setStatus("error");
      throw msg;
    }
  }, []);

  return {
    mutate,
    status,
    error,
    isPending: status === "pending",
    reset: () => setStatus("idle"),
  };
}

// ── useDeletePropertyListing ─────────────────────────────────────────────────
export function useDeletePropertyListing() {
  const [status, setStatus] = useState("idle");

  const mutate = useCallback(async (id) => {
    setStatus("pending");
    try {
      await deletePropertyListing(id);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      throw err;
    }
  }, []);

  return {
    mutate,
    isPending: status === "pending",
    status,
    reset: () => setStatus("idle"),
  };
}

// ── usePublishPropertyListing ────────────────────────────────────────────────
// Toggles a listing's public visibility. Pass `next` = true to publish, false
// to unpublish. Returns the updated listing so the caller can patch its row.
export function usePublishPropertyListing() {
  const [pendingId, setPendingId] = useState(null);

  const mutate = useCallback(async (id, next) => {
    setPendingId(id);
    try {
      return next
        ? await publishPropertyListing(id)
        : await unpublishPropertyListing(id);
    } finally {
      setPendingId(null);
    }
  }, []);

  return { mutate, pendingId, isPending: (id) => pendingId === id };
}
